import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  Button,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ScienceIcon from '@mui/icons-material/Science';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import CurrencyBitcoinIcon from '@mui/icons-material/CurrencyBitcoin';
import LogoutIcon from '@mui/icons-material/Logout';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import SettingsIcon from '@mui/icons-material/Settings';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
// Admin emails are now loaded dynamically from Firestore
import { useAdminEmails } from '../../hooks/useAdminEmails';
import { Logo } from './Logo';
import { userService } from '../../services/user';

interface NavigationItem {
  text: string;
  icon: React.ReactElement;
  path: string;
}

export const Header: React.FC = () => {
  const { adminEmails, loading: adminLoading } = useAdminEmails();
  const navigate = useNavigate();
  const location = useLocation();
  const { state, signOut } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [user, setUser] = useState<any>({});

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (state.user?.email) {
        const profile = await userService.getUserProfile(state.user.email);
        if (profile) {
          setUser(profile);
        }
      }
    };

    fetchUserProfile();
  }, [state.user?.email]);

  const isPublicRoute = ['/sign-in', '/sign-up', '/'].includes(location.pathname);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuItemClick = (path: string) => {
    handleProfileMenuClose();
    handleDrawerToggle();
    navigate(path);
  };

  const handleLogout = async () => {
    handleProfileMenuClose();
    handleDrawerToggle();
    await signOut();
    navigate('/sign-in');
  };

  const navigationItems: NavigationItem[] = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Portfolio', icon: <AccountBalanceWalletIcon />, path: '/portfolio' },
    { text: 'Labs', icon: <ScienceIcon />, path: '/labs' },
    { text: 'Live Strategies', icon: <TrendingUpIcon />, path: '/strategies' },
    { text: 'Marketplace', icon: <StorefrontIcon />, path: '/marketplace' },
  ];

  const isAdmin = state.user && adminEmails.includes(state.user.email);

  return (
    <AppBar position="static">
      <Toolbar>
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          {!isPublicRoute && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { sm: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <IconButton
            edge="start"
            color="inherit"
            aria-label="logo"
            onClick={() => navigate('/')}
            sx={{ p: 0, mr: 2 }}
          >
            <Logo />
          </IconButton>
          {!isPublicRoute && (
            <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 2 }}>
              {navigationItems.map((item) => (
                <Button
                  key={item.path}
                  color="inherit"
                  startIcon={item.icon}
                  onClick={() => handleMenuItemClick(item.path)}
                  sx={{
                    textTransform: 'none',
                    ...(location.pathname === item.path && {
                      borderBottom: '2px solid white',
                      borderRadius: 0,
                    }),
                    ...(item.text === 'Marketplace' && {
                      background: 'linear-gradient(45deg, #2E7D32 30%, #FFD700 90%)',
                      borderRadius: '20px',
                      boxShadow: '0 3px 5px 2px rgba(46, 125, 50, .3)',
                      color: 'white',
                      padding: '6px 16px',
                      '&:hover': {
                        background: 'linear-gradient(45deg, #FFD700 30%, #2E7D32 90%)',
                      },
                      ...(location.pathname === item.path && {
                        borderBottom: 'none',
                      }),
                    }),
                  }}
                >
                  {item.text}
                </Button>
              ))}
            </Box>
          )}
        </Box>

        {location.pathname === '/' && !state.user ? (
          // Show auth buttons only on landing page for non-authenticated users
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              color="inherit"
              variant="outlined"
              onClick={() => navigate('/sign-in')}
              sx={{
                borderColor: 'rgba(255, 255, 255, 0.5)',
                minWidth: '120px',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              Sign In
            </Button>
            <Button
              variant="contained"
              onClick={() => navigate('/sign-up')}
              sx={{
                backgroundColor: 'primary.light',
                minWidth: '120px',
                '&:hover': {
                  backgroundColor: 'primary.main',
                },
              }}
            >
              Create Account
            </Button>
          </Box>
        ) : !isPublicRoute ? (
          // Regular header for authenticated users
          <>
            <Typography variant="body1" sx={{ mr: 2, display: { xs: 'none', sm: 'block' } }}>
              Welcome, <span style={{ 
                background: 'linear-gradient(45deg, #2E7D32 30%, #FFD700 90%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontWeight: 'bold' 
              }}>{user.name?.split(' ')[0] || user.email?.split('@')[0] || 'Trader'}</span>
            </Typography>
            <IconButton
              edge="end"
              color="inherit"
              aria-label="account"
              onClick={handleProfileMenuOpen}
            >
              <AccountCircleIcon />
            </IconButton>
          </>
        ) : null}

        <Menu
          anchorEl={anchorEl}
          id="account-menu"
          open={Boolean(anchorEl)}
          onClose={handleProfileMenuClose}
          PaperProps={{
            sx: {
              width: 320,
              maxWidth: '100%',
              mt: 1,
            },
          }}
        >
          <MenuItem onClick={() => handleMenuItemClick('/profile')}>
            <ListItemIcon>
              <AccountCircleIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Profile Settings" />
          </MenuItem>
          <MenuItem onClick={() => handleMenuItemClick('/settings')}>
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Settings" />
          </MenuItem>
          
          {isAdmin && (
            <>
              <Divider />
              <Typography variant="subtitle2" color="textSecondary" sx={{ px: 2, py: 1 }}>
                Admin Tools
              </Typography>
              <MenuItem onClick={() => handleMenuItemClick('/admin-kraken')}>
                <ListItemIcon>
                  <VisibilityIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Kraken Admin Dashboard" />
              </MenuItem>
              <MenuItem onClick={() => handleMenuItemClick('/admin-binanceus')}>
                <ListItemIcon>
                  <VisibilityIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Binance US Admin Dashboard" />
              </MenuItem>
              <MenuItem onClick={() => handleMenuItemClick('/admin-coinbase')}>
                <ListItemIcon>
                  <VisibilityIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Coinbase Admin Dashboard" />
              </MenuItem>
              <MenuItem onClick={() => handleMenuItemClick('/admin-uniswap')}>
                <ListItemIcon>
                  <VisibilityIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Uniswap Admin Dashboard" />
              </MenuItem>
            </>
          )}
            
          <Divider />
          <Typography variant="subtitle2" color="textSecondary" sx={{ px: 2, py: 1 }}>
            Stock Brokers
          </Typography>
          <MenuItem onClick={() => handleMenuItemClick('/alpaca-config')}>
            <ListItemIcon>
              <MonetizationOnIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Alpaca Settings" />
          </MenuItem>
          <MenuItem onClick={() => handleMenuItemClick('/tdameritrade-config')}>
            <ListItemIcon>
              <MonetizationOnIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="TD Ameritrade Settings" />
          </MenuItem>

          <Divider />
          <Typography variant="subtitle2" color="textSecondary" sx={{ px: 2, py: 1 }}>
            Crypto Exchanges
          </Typography>
          <MenuItem onClick={() => handleMenuItemClick('/coinbase-config')}>
            <ListItemIcon>
              <CurrencyBitcoinIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Coinbase Settings" />
          </MenuItem>
          <MenuItem onClick={() => handleMenuItemClick('/kraken-config')}>
            <ListItemIcon>
              <CurrencyBitcoinIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Kraken Settings" />
          </MenuItem>
          <MenuItem onClick={() => handleMenuItemClick('/binanceus-config')}>
            <ListItemIcon>
              <CurrencyBitcoinIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Binance.US Settings" />
          </MenuItem>

          <Divider />
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </MenuItem>
        </Menu>

        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: 280,
              background: 'linear-gradient(180deg, #1A237E 0%, #283593 100%)',
              color: 'white'
            },
          }}
        >
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
            <Logo />
            <Typography variant="h6" sx={{ ml: 2 }}>Strategiz</Typography>
          </Box>
          <List>
            {navigationItems.map((item) => (
              <ListItem 
                key={item.path}
                onClick={() => handleMenuItemClick(item.path)}
                sx={{ 
                  cursor: 'pointer',
                  ...(location.pathname === item.path && {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  })
                }}
              >
                <ListItemIcon sx={{ color: 'white' }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItem>
            ))}
          </List>
          <Divider sx={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
          <List>
            <ListItem 
              onClick={() => handleMenuItemClick('/profile')}
              sx={{ cursor: 'pointer' }}
            >
              <ListItemIcon sx={{ color: 'white' }}><AccountCircleIcon /></ListItemIcon>
              <ListItemText primary="Profile Settings" />
            </ListItem>
            <ListItem 
              onClick={() => handleMenuItemClick('/settings')}
              sx={{ cursor: 'pointer' }}
            >
              <ListItemIcon sx={{ color: 'white' }}><SettingsIcon /></ListItemIcon>
              <ListItemText primary="Settings" />
            </ListItem>
            
            {/* Admin Tools Section - Only visible to admin users */}
            {isAdmin && (
              <>
                <Divider sx={{ backgroundColor: 'rgba(255,255,255,0.1)', my: 1 }} />
                <Typography variant="subtitle2" sx={{ px: 2, py: 1, opacity: 0.7 }}>
                  Admin Tools
                </Typography>
                <ListItem 
                  onClick={() => handleMenuItemClick('/admin-kraken')}
                  sx={{ cursor: 'pointer' }}
                >
                  <ListItemIcon sx={{ color: 'white' }}><VisibilityIcon /></ListItemIcon>
                  <ListItemText primary="Kraken Admin Dashboard" />
                </ListItem>
                <ListItem 
                  onClick={() => handleMenuItemClick('/admin-binanceus')}
                  sx={{ cursor: 'pointer' }}
                >
                  <ListItemIcon sx={{ color: 'white' }}><VisibilityIcon /></ListItemIcon>
                  <ListItemText primary="Binance US Admin Dashboard" />
                </ListItem>
                <ListItem 
                  onClick={() => handleMenuItemClick('/admin-coinbase')}
                  sx={{ cursor: 'pointer' }}
                >
                  <ListItemIcon sx={{ color: 'white' }}><VisibilityIcon /></ListItemIcon>
                  <ListItemText primary="Coinbase Admin Dashboard" />
                </ListItem>
                <ListItem 
                  onClick={() => handleMenuItemClick('/admin-uniswap')}
                  sx={{ cursor: 'pointer' }}
                >
                  <ListItemIcon sx={{ color: 'white' }}><VisibilityIcon /></ListItemIcon>
                  <ListItemText primary="Uniswap Admin Dashboard" />
                </ListItem>
              </>
            )}
            
            <Divider sx={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
            <ListItem 
              onClick={handleLogout}
              sx={{ cursor: 'pointer' }}
            >
              <ListItemIcon sx={{ color: 'white' }}><LogoutIcon /></ListItemIcon>
              <ListItemText primary="Sign Out" />
            </ListItem>
          </List>
        </Drawer>
      </Toolbar>
    </AppBar>
  );
};